/**
 * Audits whose analyses are being run, so the app can tell the user when each
 * analysis starts and finishes — wherever they are in the app, not only on
 * the Analyses page. `components/AnalysisRunWatcher.tsx` does the watching.
 *
 * In memory only: a reload stops the notifications, never the runs.
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export interface WatchedAudit {
	/** True when the run was started from this browser: "started running" is
	 *  announced. False when a run already in progress was found on a page. */
	announceStart: boolean;
	/** When watching began (ms), so a run the server never picks up is let go. */
	since: number;
}

const slice = createSlice({
	name: 'runWatch',
	initialState: { audits: {} as Record<string, WatchedAudit> },
	reducers: {
		watchAudit(
			state,
			action: PayloadAction<{ auditId: string; announceStart: boolean }>,
		) {
			const { auditId, announceStart } = action.payload;
			const current = state.audits[auditId];
			state.audits[auditId] = {
				announceStart: announceStart || Boolean(current?.announceStart),
				since: current?.since ?? Date.now(),
			};
		},
		unwatchAudit(state, action: PayloadAction<string>) {
			delete state.audits[action.payload];
		},
	},
});

export const { watchAudit, unwatchAudit } = slice.actions;
export default slice.reducer;
